import { h } from 'preact';
import { useState } from 'preact/hooks';

export interface CascadeEntry {
  tier: string;
  costUsd: number;
  inputTokens: number;
  outputTokens: number;
  requests: number;
}

export interface UsageBucket {
  date: string;
  totalCostUsd: number;
  sessions: number;
  turns: number;
  byUser: Array<{ userId: string; email: string; costUsd: number; sessions: number }>;
  byProvider: Array<{ provider: string; costUsd: number }>;
  cascade?: CascadeEntry[];
}

interface Props {
  buckets: UsageBucket[];
  period: string;
}

export function UsageChart({ buckets, period }: Props) {
  const [tooltip, setTooltip] = useState<{ x: number; y: number; bucket: UsageBucket } | null>(null);

  if (!buckets.length) {
    return <div class="usage-empty">No usage data for this period.</div>;
  }

  const maxCost = Math.max(...buckets.map((b) => b.totalCostUsd), 0.0001);

  const fmtLabel = (date: string) => {
    const d = new Date(date + 'T12:00:00Z');
    if (period === 'month') return d.toLocaleString('default', { month: 'short', year: '2-digit', timeZone: 'UTC' });
    if (period === 'week') return `W${Math.ceil(d.getUTCDate() / 7)} ${d.toLocaleString('default', { month: 'short', timeZone: 'UTC' })}`;
    return d.toLocaleString('default', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  const fmtCost = (c: number) => c < 0.01 ? `$${c.toFixed(4)}` : `$${c.toFixed(3)}`;
  const fmtTokens = (t: number) => t >= 1_000_000 ? `${(t / 1_000_000).toFixed(1)}M` : t >= 1000 ? `${(t / 1000).toFixed(1)}k` : `${t}`;

  const handleMouseEnter = (e: MouseEvent, bucket: UsageBucket) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setTooltip({ x: rect.left + rect.width / 2, y: rect.top - 8, bucket });
  };

  // Segment widths: anthropic portion + google portion
  const getSegments = (b: UsageBucket) => {
    const anthropic = b.byProvider.find((p) => p.provider === 'anthropic')?.costUsd || 0;
    const google = b.byProvider.find((p) => p.provider === 'google')?.costUsd || 0;
    const other = b.totalCostUsd - anthropic - google;
    const total = b.totalCostUsd || 0.0001;
    return [
      { pct: (anthropic / total) * 100, cls: 'seg-anthropic' },
      { pct: (google / total) * 100, cls: 'seg-google' },
      { pct: (other / total) * 100, cls: 'seg-other' },
    ].filter((s) => s.pct > 0);
  };

  // Determine which providers appear in the data
  const hasAnth = buckets.some((b) => b.byProvider.some((p) => p.provider === 'anthropic' && p.costUsd > 0));
  const hasGoogle = buckets.some((b) => b.byProvider.some((p) => p.provider === 'google' && p.costUsd > 0));

  return (
    <div class="usage-chart">
      {(hasAnth || hasGoogle) && (
        <div class="usage-legend">
          {hasAnth && <span class="usage-legend-item"><span class="usage-legend-dot seg-anthropic" />Anthropic</span>}
          {hasGoogle && <span class="usage-legend-item"><span class="usage-legend-dot seg-google" />Google / Vertex</span>}
        </div>
      )}
      {buckets.map((b) => {
        const barPct = (b.totalCostUsd / maxCost) * 100;
        const segments = getSegments(b);
        return (
          <div
            key={b.date}
            class="usage-row"
            onMouseEnter={(e) => handleMouseEnter(e as MouseEvent, b)}
            onMouseLeave={() => setTooltip(null)}
          >
            <span class="usage-label">{fmtLabel(b.date)}</span>
            <div class="usage-bar-track">
              <div class="usage-bar" style={{ width: `${barPct}%` }}>
                {segments.map((s, i) => (
                  <div key={i} class={`usage-bar-segment ${s.cls}`} style={{ width: `${s.pct}%` }} />
                ))}
              </div>
            </div>
            <span class="usage-cost">{b.totalCostUsd > 0 ? fmtCost(b.totalCostUsd) : '—'}</span>
          </div>
        );
      })}
      {tooltip && (
        <div
          class="usage-tooltip"
          style={{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }}
        >
          <div class="usage-tooltip-date">{tooltip.bucket.date}</div>
          <div class="usage-tooltip-row"><span>Cost</span><span>{fmtCost(tooltip.bucket.totalCostUsd)}</span></div>
          <div class="usage-tooltip-row"><span>Sessions</span><span>{tooltip.bucket.sessions}</span></div>
          <div class="usage-tooltip-row"><span>Turns</span><span>{tooltip.bucket.turns}</span></div>
          {tooltip.bucket.byProvider.map((p) => (
            <div key={p.provider} class="usage-tooltip-row provider">
              <span>{p.provider}</span><span>{fmtCost(p.costUsd)}</span>
            </div>
          ))}
          {tooltip.bucket.cascade && tooltip.bucket.cascade.length > 0 && (
            <>
              <div class="usage-tooltip-divider" />
              <div class="usage-tooltip-label">Cascade</div>
              {tooltip.bucket.cascade.map((c) => (
                <div key={c.tier} class="usage-tooltip-row cascade">
                  <span>{c.tier}</span>
                  <span>{fmtCost(c.costUsd)} ({fmtTokens(c.inputTokens + c.outputTokens)})</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
